import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, Loader2, Square } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const VoiceRecorder = ({ onTranscription, fieldName, disabled }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await sendAudioForTranscription(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info('🎤 Registrazione in corso... Parla ora');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Impossibile accedere al microfono. Verifica i permessi.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const sendAudioForTranscription = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        try {
          const response = await axios.post(
            `${API}/api/voice-transcription`,
            {
              audio_base64: base64Audio,
              audio_format: 'webm',
              context: 'cartella_clinica'
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.success) {
            const text = response.data.transcription;
            onTranscription(text);
            toast.success(`✅ Trascritto: "${text.substring(0, 50)}${text.length > 50 ? '...' : ''}"`);
          } else {
            toast.error('Errore nella trascrizione');
          }
        } catch (error) {
          console.error('Transcription error:', error);
          toast.error(error.response?.data?.detail || 'Errore trascrizione vocale');
        } finally {
          setIsProcessing(false);
        }
      };
    } catch (error) {
      console.error('Audio processing error:', error);
      toast.error('Errore elaborazione audio');
      setIsProcessing(false);
    }
  };

  return (
    <button
      type="button"
      onClick={isRecording ? stopRecording : startRecording}
      disabled={disabled || isProcessing}
      className={`p-2 rounded-lg transition-colors ${
        isRecording 
          ? 'bg-red-500 text-white animate-pulse' 
          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
      } ${disabled || isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
      title={isRecording ? 'Stop registrazione' : 'Registra voce'}
    >
      {isProcessing ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isRecording ? (
        <Square className="w-4 h-4" />
      ) : (
        <Mic className="w-4 h-4" />
      )}
    </button>
  );
};

export default VoiceRecorder;
